// ============================================================================
// TranscriptPanel — панель личной расшифровки речи (расширение плеера):
//   • «главы» — фразовые сегменты STT НАД словами: клик перематывает плеер
//     на начало главы, активная глава подсвечивается по времени; сегментов
//     нет — блока глав НЕТ (фейковые не создаются);
//   • слова — кликабельные токены: клик перематывает на слово, активное
//     слово подсвечивается синхронно с воспроизведением.
// Метки приходят С СЕРВЕРА; панель вне проигрывателя — просто текст.
// Синхронизация — по timeupdate плеера (контекст MediaPlayerApi): активные
// глава/слово ищутся бинарным поиском (O(log n)); токены слов разбиты на
// мемоизированные сегменты (при смене слова обновляются только два), ряд
// глав мемоизирован отдельно (перерисовка только на смене активной главы).
// ============================================================================

import { memo, useMemo } from 'react';
import { useMediaPlayerApi } from '@/components/media/mediaPlayerContext';
import { findActiveWordIndex, type TranscriptWordData } from '@/lib/transcriptWords';
import { findActiveChapterIndex, type TranscriptChapterData } from '@/lib/transcriptChapters';
import { fmt } from '@/components/media/mediaPlayerParts';

/** Слов в одном мемоизированном сегменте: точечные обновления по тикам. */
const SEGMENT_SIZE = 64;

/** Ряд глав: чипы с началом («0:12 текст»); активная глава подсвечена. */
const ChapterChips = memo(function ChapterChips({ chapters, activeIndex, onSeek }: {
  chapters: readonly TranscriptChapterData[];
  activeIndex: number;
  onSeek: (seconds: number) => void;
}) {
  return (
    <div className="pb-1">
      <div className="mb-0.5 text-[10px] uppercase tracking-wide opacity-70">Главы</div>
      <div className="flex gap-1 overflow-x-auto">
        {chapters.map((c, i) => (
          <button
            key={`${c.start}:${i}`}
            type="button"
            onClick={() => onSeek(c.start)}
            title={c.text}
            className={
              i === activeIndex
                ? 'shrink-0 max-w-[180px] truncate rounded-full bg-primary/25 px-2 py-0.5 text-foreground'
                : 'shrink-0 max-w-[180px] truncate rounded-full border px-2 py-0.5 hover:text-foreground'
            }
          >
            <span className="mr-1 opacity-60">{fmt(c.start)}</span>{c.text}
          </button>
        ))}
      </div>
    </div>
  );
}, (prev, next) =>
  prev.chapters === next.chapters
  && prev.activeIndex === next.activeIndex
  && prev.onSeek === next.onSeek);

interface SegmentProps {
  words: readonly TranscriptWordData[];
  offset: number;
  /** индекс активного слова ВНУТРИ сегмента (-1 — активного нет). */
  activeLocal: number;
  onSeekWord: (seconds: number) => void;
}

const Segment = memo(function TranscriptSegment({ words, offset, activeLocal, onSeekWord }: SegmentProps) {
  return (
    <span>
      {words.map((w, i) => (
        <button
          key={offset + i}
          type="button"
          onClick={() => onSeekWord(w.start)}
          title="Перейти к этому слову"
          className={
            i === activeLocal
              ? 'rounded bg-primary/25 text-foreground'
              : 'cursor-pointer rounded hover:text-foreground'
          }
        >
          {w.word}
        </button>
      ))}
    </span>
  );
}, (prev, next) =>
  prev.words === next.words
  && prev.offset === next.offset
  && prev.activeLocal === next.activeLocal
  && prev.onSeekWord === next.onSeekWord);

export function TranscriptPanel({ text, words, chapters }: {
  text: string;
  words: readonly TranscriptWordData[];
  /** Главы-сегменты (пусто — блок глав не показывается) */
  chapters: readonly TranscriptChapterData[];
}) {
  const api = useMediaPlayerApi();
  const segments = useMemo(() => {
    const out: { words: TranscriptWordData[]; offset: number }[] = [];
    for (let i = 0; i < words.length; i += SEGMENT_SIZE) {
      out.push({ words: words.slice(i, i + SEGMENT_SIZE), offset: i });
    }
    return out;
  }, [words]);

  // Меток нет (и глав нет) либо панель вне проигрывателя — текст как раньше.
  if ((words.length === 0 && chapters.length === 0) || !api) {
    return (
      <div className="mt-1 border-t pt-1 text-[11px] leading-snug text-muted-foreground">
        {text || 'Речь не распознана'}
      </div>
    );
  }

  const activeWord = findActiveWordIndex(words, api.current);
  const activeChapter = findActiveChapterIndex(chapters, api.current);
  return (
    <div className="mt-1 border-t pt-1 text-[11px] leading-snug text-muted-foreground">
      {chapters.length > 0 && (
        <ChapterChips chapters={chapters} activeIndex={activeChapter} onSeek={api.seek} />
      )}
      <div className="max-h-40 overflow-y-auto whitespace-pre-wrap break-words">
        {words.length > 0 ? segments.map((s) => {
          const inSegment = activeWord >= s.offset && activeWord < s.offset + s.words.length;
          return (
            <Segment
              key={s.offset}
              words={s.words}
              offset={s.offset}
              activeLocal={inSegment ? activeWord - s.offset : -1}
              onSeekWord={api.seek}
            />
          );
        }) : text}
      </div>
    </div>
  );
}
